import * as React from 'react';
import * as _ from 'lodash';
import { Col, FormControl, Button, Row, Panel, ControlLabel, Radio, FormGroup, Label, Glyphicon } from 'react-bootstrap';
import { IListItem, IReferralConfig } from '@nkr/models/index'; //this is for loading of the initial data to page
import { FormWrapper } from './styles';
import { IValidation } from './validation';
import CancelRejectionList from './cancer-rejection-list';
import KidneyStones from './kidney-stones';
import ReferralSources from './referral-sources';
import Workflow from './workflow';
import KidneyDonorsOnly from './kidney-donors-only';
import LiverDonorsOnly from './liver-donors-only';
import AdminOnly from './admin-only';
import Dash from './dash';
import { useAppHookContext } from '@nkr/utils/rest-api';
import { OrganType } from '@nkr/enums/organ-type';

interface IProps {
    data: IReferralConfig,
    isAdmin: boolean,
    isAdminUser: boolean,
    centerAdmin?: boolean,
    validation: IValidation,
    dirty: boolean,
    lookUpData: {
        statusList: IListItem[],
        followUpList: IListItem[],
        diabetesStatusList: IListItem[],
        customKitsList: IListItem[],
        preWorkupLabConfigList: IListItem[],
        cancerTypeList: IListItem[],
        fvVideoList: IListItem[],
        svVideoList: IListItem[],
        donorConnectGroups: IListItem[]
    },
    onSave: () => void,
    onChange: (data: IReferralConfig) => void
}

const ReferralConfigForm = (props: IProps) => {
    const { app: { site } } = useAppHookContext();

    const handleDataChange = (e: React.FormEvent<FormControl & HTMLInputElement & HTMLSelectElement>) => {
        let id = e.currentTarget.id;
        let value = e.currentTarget.value;
        let data = _.cloneDeep(props.data);

        data[id] = typeof value === 'number' ? parseInt(value) : value;

        props.onChange(data);
    };

    const handleRadioChange = (e: React.FormEvent<Radio & HTMLInputElement>) => {
        let name = e.currentTarget.name;
        let value = e.currentTarget.value;
        let data = _.cloneDeep(props.data);

        data[name] = value.length > 0 ? parseInt(value) : null;

        props.onChange(data);
    };

    const handleCancerRuleChange = (index, field, target) => {
        let data = _.cloneDeep(props.data);

        data.cancerRejectionList.map((x, itemIndex) => {
            if (itemIndex == index) {
                x[field] = target.value;
            }
            return x;
        });

        props.onChange(data);
    };

    const handleKidneyStoneChange = (index: number, value: number) => {
        let data = _.cloneDeep(props.data);

        data.kidneyStoneRejectionList = data.kidneyStoneRejectionList.map(x => {
            if (x.index == index) {
                x.value = value;
            }
            return x;
        });

        props.onChange(data);
    };

    const handleReferralSourceChange = (index: number, value: string) => {
        let data = _.cloneDeep(props.data);

        data.referralSources = data.referralSources.map((x, itemIndex) => {
            if (itemIndex == index) {
                x = value;
            }

            return x;
        });

        props.onChange(data);
    };

    const handleAddCancerRule = () => {
        let data = _.cloneDeep(props.data);

        data.cancerRejectionList.push({ id: 5, remission: null });

        props.onChange(data);
    };

    const handleAddReferralSource = () => {
        let data = _.cloneDeep(props.data);

        data.referralSources.push(null);

        props.onChange(data);
    };

    const handleRemoveRow = (index, type) => {
        let data = _.cloneDeep(props.data);

        if (type === 'cancer') {
            data.cancerRejectionList = data.cancerRejectionList.filter((x, itemIndex) => itemIndex != index);
        } else if (type === 'referral') {
            data.referralSources = data.referralSources.filter((x, itemIndex) => itemIndex != index);
        }

        props.onChange(data);
    };

    const disableSubmitButton = false;

    return (
        <div className='container-fluid' style={{ paddingBottom: 10 }}>
            <FormWrapper horizontal>
                <Row>
                    <Col xs={12} lg={6}>
                        {site.organType == OrganType.Kidney && (
                            <Row>
                                <Col xs={12} lg={12}>
                                    <Workflow
                                        data={props.data}
                                        validation={props.validation}
                                        dirty={props.dirty}
                                        statusList={props.lookUpData.statusList}
                                        onRadioChange={handleRadioChange}
                                        onDataChange={handleDataChange} />
                                </Col>
                            </Row>
                        )}

                        {site.organType == OrganType.Kidney && (
                            <Row>
                                <Col xs={12} lg={12}>
                                    <KidneyDonorsOnly
                                        data={props.data}
                                        validation={props.validation}
                                        dirty={props.dirty}
                                        onRadioChange={handleRadioChange}
                                        onDataChange={handleDataChange} />
                                </Col>
                            </Row>
                        )}

                        <Row>
                            <Col xs={12} lg={12}>
                                <LiverDonorsOnly
                                    data={props.data}
                                    validation={props.validation}
                                    dirty={props.dirty}
                                    onRadioChange={handleRadioChange}
                                    onDataChange={handleDataChange} />
                            </Col>
                        </Row>
                    </Col>
                    <Col xs={12} lg={6}>
                        {(props.isAdminUser || props.centerAdmin) && (
                            <AdminOnly
                                data={props.data}
                                validation={props.validation}
                                dirty={props.dirty}
                                svVideoList={props.lookUpData.svVideoList}
                                dcGroups={props.lookUpData.donorConnectGroups}
                                onRadioChange={handleRadioChange}
                                onDataChange={handleDataChange} />
                        )}

                        <Dash
                            data={props.data}
                            validation={props.validation}
                            dirty={props.dirty}
                            preWorkupLabConfigList={props.lookUpData.preWorkupLabConfigList}
                            onRadioChange={handleRadioChange}
                            onDataChange={handleDataChange} />

                        {site.organType == OrganType.Kidney && (
                            <Panel style={{ marginTop: '20px' }}>
                                <Panel.Heading>Recipient Settings</Panel.Heading>
                                <Panel.Body>
                                    <FormGroup validationState={props.dirty && !props.validation.recipientMaxAge ? 'error' : null}>
                                        <Col xs={12} lg={6} componentClass={ControlLabel}>Max Age of the Recipient?</Col>
                                        <Col xs={12} lg={6}>
                                            <FormControl
                                                type="text"
                                                id="recipientMaxAge"
                                                style={{ width: 300 }}
                                                value={props.data.recipientMaxAge}
                                                onChange={handleDataChange}
                                            />
                                        </Col>
                                    </FormGroup>

                                </Panel.Body>
                            </Panel>
                        )}

                        <Panel>
                            <Panel.Heading>Donor Follow-Up Settings</Panel.Heading>
                            <Panel.Body>
                                <FormGroup validationState={props.dirty && !props.validation.yearsForFollowUpLabs ? 'error' : null}>
                                    <Col xs={12} lg={6} componentClass={ControlLabel}>How many years will you do Follow-up Labs?</Col>
                                    <Col xs={12} lg={5}>
                                        <FormControl
                                            componentClass='select'
                                            name='yearsForFollowUpLabs'
                                            id="yearsForFollowUpLabs"
                                            value={props.data.yearsForFollowUpLabs}
                                            onChange={handleDataChange}
                                        >
                                            {props.lookUpData.followUpList.map(i =>
                                                <option key={'followup-' + i.id} value={i.id}>{i.description}</option>
                                            )}
                                        </FormControl>
                                    </Col>
                                </FormGroup>
                            </Panel.Body>
                        </Panel>
                    </Col>
                </Row>
                <Row>
                    <Col xs={12} lg={12}>
                        <CancelRejectionList
                            cancerRejectionList={props.data.cancerRejectionList}
                            cancerTypeList={props.lookUpData.cancerTypeList}
                            statusList={props.lookUpData.statusList}
                            onChange={handleCancerRuleChange}
                            onAdd={handleAddCancerRule}
                            onRemove={handleRemoveRow} />

                    </Col>
                </Row>
                {site.organType == OrganType.Kidney && (
                    <Row>
                        <Col xs={12} lg={12}>
                            <KidneyStones
                                kidneyStoneRejectionList={props.data.kidneyStoneRejectionList}
                                statusList={props.lookUpData.statusList}
                                onChange={handleKidneyStoneChange} />
                        </Col>
                    </Row>
                )}
                <Row>
                    <Col xs={12} lg={12}>
                        <ReferralSources
                            referralSourceList={props.data.referralSources}
                            onChange={handleReferralSourceChange}
                            onRemove={handleRemoveRow}
                            onAdd={handleAddReferralSource} />
                    </Col>
                </Row>
                <Row>
                    <Col xs={12} lg={12} className='text-center'>
                        <div style={{ display: 'flex', justifyContent: 'center' }}>
                            <Button bsStyle='primary' bsSize='lg' onClick={props.onSave} disabled={disableSubmitButton} style={{ width: '200px' }}>SAVE</Button>
                        </div>
                    </Col>
                </Row>
            </FormWrapper>
        </div>
    );
}

export default ReferralConfigForm;